import React from 'react';
import { Type, Minus, MailOpen } from 'lucide-react';

/**
 * Компонент для настройки параметров шрифта
 */
const FontParametersSection = ({ formData, handleInputChange, errors, fontFamilies }) => {
  // Варианты единиц измерения размера шрифта
  const fontSizeUnits = [
    { value: 'px', label: 'px' },
    { value: 'em', label: 'em' },
    { value: 'rem', label: 'rem' },
    { value: 'pt', label: 'pt' },
    { value: '%', label: '%' },
    { value: 'vw', label: 'vw' }
  ];

  // Предустановленные варианты насыщенности шрифта
  const fontWeightOptions = [
    { value: '300', label: 'Light' },
    { value: '400', label: 'Regular' },
    { value: '500', label: 'Medium' },
    { value: '600', label: 'SemiBold' },
    { value: '700', label: 'Bold' },
    { value: '800', label: 'ExtraBold' }
  ];

  return (
    <div className="mb-6 bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Type className="w-5 h-5 mr-2 text-indigo-500" />
        Параметры шрифта
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Размер шрифта */}
        <div>
          <label htmlFor="fontSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Размер шрифта
          </label>
          <div className="flex">
            <input
              type="text"
              id="fontSize"
              name="fontSize"
              value={formData.fontSize}
              onChange={handleInputChange}
              className={`form-input w-full rounded-r-none ${errors.fontSize ? 'input-error' : ''}`}
              placeholder="Размер"
            />
            <select
              name="fontSizeUnit"
              value={formData.fontSizeUnit}
              onChange={handleInputChange}
              className="form-select rounded-l-none w-20 border-l-0"
            >
              {fontSizeUnits.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>
          {errors.fontSize && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.fontSize}
            </p>
          )}
          
          {/* Советник по размеру */}
          <div className="mt-2 text-xs text-indigo-600 dark:text-indigo-400">
            <p>
              {formData.fontSizeUnit === 'px' && 'Рекомендуемый размер для веб: 16-18px'}
              {formData.fontSizeUnit === 'em' && 'Относительный размер к родителю (1em = размеру родителя)'}
              {formData.fontSizeUnit === 'rem' && 'Относительный размер к корневому элементу (1rem = 16px)'}
              {formData.fontSizeUnit === 'pt' && 'Точки используются в основном для печатных материалов'}
              {formData.fontSizeUnit === '%' && 'Относительный размер в процентах от родительского элемента'}
              {formData.fontSizeUnit === 'vw' && 'Относительный размер в процентах от ширины экрана'}
            </p>
          </div>
        </div>

        {/* Семейство шрифта */}
        <div>
          <label htmlFor="fontFamily" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Семейство шрифта
          </label>
          <select
            id="fontFamily"
            name="fontFamily"
            value={formData.fontFamily}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            {fontFamilies.map((font) => (
              <option key={font.id} value={font.id} style={{ fontFamily: font.id }}>
                {font.name} ({font.type})
              </option>
            ))}
          </select>
        </div>

        {/* Насыщенность шрифта */}
        <div>
          <label htmlFor="fontWeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Насыщенность шрифта
          </label>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
            {fontWeightOptions.map((weight) => (
              <label 
                key={weight.value}
                className={`
                  text-center py-1.5 px-1 text-xs rounded cursor-pointer 
                  ${formData.fontWeight === weight.value 
                    ? 'bg-indigo-100 dark:bg-indigo-800 font-semibold text-indigo-700 dark:text-indigo-300' 
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600'
                  }
                `}
              >
                <input
                  type="radio"
                  name="fontWeight"
                  value={weight.value}
                  checked={formData.fontWeight === weight.value}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                {weight.label}
              </label>
            ))}
          </div>
          {errors.fontWeight && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.fontWeight}
            </p>
          )}
        </div>

        {/* Высота строки */}
        <div>
          <label htmlFor="lineHeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Minus className="w-4 h-4 mr-1 text-indigo-500" />
            Высота строки
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              id="lineHeight"
              name="lineHeight"
              min="1"
              max="2.5"
              step="0.1"
              value={formData.lineHeight}
              onChange={handleInputChange}
              className="flex-grow h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <input
              type="text"
              name="lineHeight"
              value={formData.lineHeight}
              onChange={handleInputChange}
              className={`form-input w-16 text-center ${errors.lineHeight ? 'input-error' : ''}`}
            />
          </div>
          {errors.lineHeight && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.lineHeight}
            </p>
          )}
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>Узко</span>
            <span>Оптимально</span>
            <span>Широко</span>
          </div>
        </div>

        {/* Межбуквенное расстояние */}
        <div>
          <label htmlFor="letterSpacing" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <MailOpen className="w-4 h-4 mr-1 text-indigo-500" />
            Межбуквенное расстояние (em)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              id="letterSpacing"
              name="letterSpacing"
              min="-0.05"
              max="0.2"
              step="0.01"
              value={formData.letterSpacing}
              onChange={handleInputChange}
              className="flex-grow h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <input
              type="text"
              name="letterSpacing"
              value={formData.letterSpacing}
              onChange={handleInputChange}
              className={`form-input w-16 text-center ${errors.letterSpacing ? 'input-error' : ''}`}
            />
          </div>
          {errors.letterSpacing && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.letterSpacing}
            </p>
          )}
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>Сжато</span>
            <span>Нормально</span>
            <span>Разрежено</span>
          </div>
        </div>
      </div>

      {/* Предпросмотр шрифта */}
      <div className="mt-4 p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
        <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Предпросмотр шрифта:</div>
        <div 
          className="text-gray-900 dark:text-white"
          style={{
            fontFamily: formData.fontFamily,
            fontWeight: formData.fontWeight,
            fontSize: `${formData.fontSize}${formData.fontSizeUnit}`,
            lineHeight: formData.lineHeight,
            letterSpacing: `${formData.letterSpacing}em`
          }}
        >
          Аа Бб Вв Гг Дд Ее Ёё Жж Зз Ии Йй Кк Лл Мм Нн
        </div>
      </div>
    </div>
  );
};

export default FontParametersSection;